import styled from "@emotion/styled";
import { STEP_MINT } from "@stepfinance/common";
import type { CollapseProps } from "antd";
import type { FC } from "react";
import { useMemo, useState } from "react";

import { ReactComponent as RewardOptionsIcon } from "../../../assets/reward-options.svg";
import { ReactComponent as Chevron } from "../../../assets/selection-chevron.svg";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import { useRewards } from "../../../contexts/reward";
import type { RewardOption } from "../../../hooks";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { TokenSelectModal } from "../../SwapAmountInput/TokenSelectModal";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { ExerciseOption } from "./ExerciseOption";
import { getOptionValue, RewardContractTable } from "./RewardContractTable";

export const RewardOptions: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const [exerciseOption, setExerciseOption] = useState<RewardOption | null>(null);

  const { rewardOptions: contracts } = useRewards();
  const { priceInUSD } = useMarkets();
  const stepPrice = priceInUSD(STEP_MINT.toBase58());
  const totalOptionsValue = useMemo(
    () => {
      if (stepPrice === null) {
        return null;
      }
      return contracts.reduce((acc, val) => acc + getOptionValue(val, stepPrice), 0);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contracts.length, stepPrice],
  );

  const {
    currentLanguageData: {
      pages: {
        dashboard: {
          modules: {
            rewardOptions: { title: rewardOptionsTitle, claimYourRewardOption },
          },
        },
      },
    },
  } = useLanguageContext();

  const hasContracts = contracts.length > 0;
  return hasContracts ? (
    <RewardOptionsContainer>
      <ChevronCollapse
        activePanels={activePanels}
        panelKey={getPanelKey("RewardOptions")}
        onChange={onPanelClick}
        title={rewardOptionsTitle}
        value={totalOptionsValue}
        TitleIcon={RewardOptionsIcon}
        isUnclaimedValue={true}
      >
        <RewardContractTable contracts={contracts} stepPrice={stepPrice} setExerciseOption={setExerciseOption} />
      </ChevronCollapse>
      <TokenSelectModal
        title={claimYourRewardOption}
        onClose={() => setExerciseOption(null)}
        width={500}
        visible={!!exerciseOption}
      >
        {exerciseOption && (
          <ExerciseOption
            rewardOption={exerciseOption}
            stepPrice={stepPrice}
            closeModal={() => setExerciseOption(null)}
          />
        )}
      </TokenSelectModal>
    </RewardOptionsContainer>
  ) : null;
};

const ChevronWrapper = styled.div<{ isActive?: boolean }>`
  transform: ${({ isActive }) => (isActive ? "matrix(1, 0, 0, -1, 0, 0)" : undefined)};
  margin-top: ${({ isActive }) => (isActive ? "-5px" : "6px")};
`;

export const getExpandIcon: CollapseProps["expandIcon"] = ({ isActive }) => {
  return (
    <div>
      <ChevronWrapper isActive={isActive}>
        <Chevron />
      </ChevronWrapper>
    </div>
  );
};

const RewardOptionsContainer = styled.div`
  margin-top: 20px;
`;
